import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import WhatsAppButton from "./components/WhatsAppButton";
import MobileCTABar from "./components/MobileCTABar";
import CanonicalHead from "./components/CanonicalHead";
import Redirect from "./components/Redirect";
import BlogSlugRedirect from "./components/BlogSlugRedirect";
import { lazy, Suspense, useEffect, useState } from "react";

// Eagerly load Home for fast LCP
import Home from "./pages/Home";

// Lazy load all other pages to reduce initial bundle size
const Services = lazy(() => import("./pages/Services"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const SqueezePage = lazy(() => import("./pages/SqueezePage"));
const SqueezeHairLoss = lazy(() => import("./pages/SqueezeHairLoss"));
const SqueezeAcne = lazy(() => import("./pages/SqueezeAcne"));
const SqueezeMoles = lazy(() => import("./pages/SqueezeMoles"));
const SqueezeLaser = lazy(() => import("./pages/SqueezeLaser"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Booking = lazy(() => import("./pages/Booking"));
const Videos = lazy(() => import("./pages/Videos"));
const AquaPure = lazy(() => import("./pages/AquaPure"));
const AestheticTreatments = lazy(() => import("./pages/AestheticTreatments"));
const CO2Laser = lazy(() => import("./pages/CO2Laser"));
const Tarifas = lazy(() => import("./pages/Tarifas"));
const BotoxBarcelona = lazy(() => import("./pages/BotoxBarcelona"));
const PRPBarcelona = lazy(() => import("./pages/PRPBarcelona"));
const AcneBarcelona = lazy(() => import("./pages/AcneBarcelona"));
const HairLossBarcelona = lazy(() => import("./pages/HairLossBarcelona"));
const AntiAgingBarcelona = lazy(() => import("./pages/AntiAgingBarcelona"));
const DermatologistBarcelona = lazy(() => import("./pages/DermatologistBarcelona"));
const MoleCheckBarcelona = lazy(() => import("./pages/MoleCheckBarcelona"));
const SkinCancerScreeningBarcelona = lazy(() => import("./pages/SkinCancerScreeningBarcelona"));
const MelanomaSpecialistBarcelona = lazy(() => import("./pages/MelanomaSpecialistBarcelona"));
const HairLossTreatmentBarcelona = lazy(() => import("./pages/HairLossTreatmentBarcelona"));
const AcneTreatmentBarcelona = lazy(() => import("./pages/AcneTreatmentBarcelona"));
const RosaceaTreatmentBarcelona = lazy(() => import("./pages/RosaceaTreatmentBarcelona"));
const BotoxBarcelonaSqueeze = lazy(() => import("./pages/BotoxBarcelonaSqueeze"));
const EnglishSpeakingDermatologistBarcelona = lazy(() => import("./pages/EnglishSpeakingDermatologistBarcelona"));
const Gracias = lazy(() => import("./pages/Gracias"));
const NotFound = lazy(() => import("./pages/NotFound"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-pulse text-primary text-lg">Cargando...</div>
    </div>
  );
}
function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* English routes (/en/*) - English slugs */}
        <Route path={"/en"} component={Home} />
        <Route path={"/en/services"} component={Services} />
        <Route path={"/en/faq"} component={FAQ} />
        <Route path={"/en/blog/:slug"} component={BlogDetail} />
        <Route path={"/en/blog"} component={Blog} />
        <Route path={"/en/contact"} component={Contact} />
        <Route path={"/en/about"} component={About} />
        <Route path={"/en/squeeze"} component={SqueezePage} />
        <Route path={"/en/squeeze/hair-loss"} component={SqueezeHairLoss} />
        <Route path={"/en/squeeze/acne"} component={SqueezeAcne} />
        <Route path={"/en/squeeze/moles"} component={SqueezeMoles} />
        <Route path={"/en/squeeze/laser"} component={SqueezeLaser} />
        <Route path={"/en/testimonials"} component={Testimonials} />
        <Route path={"/en/booking"} component={Booking} />
        <Route path={"/en/videos"} component={Videos} />
        <Route path={"/en/aquapure"} component={AquaPure} />
        <Route path={"/en/aesthetic-treatments"} component={AestheticTreatments} />
        <Route path={"/en/co2-laser"} component={CO2Laser} />
        <Route path={"/en/botox-barcelona"} component={BotoxBarcelona} />
        <Route path={"/en/prp-barcelona"} component={PRPBarcelona} />
        <Route path={"/en/acne-treatment-barcelona"} component={AcneBarcelona} />
        <Route path={"/en/hair-loss-treatment-barcelona"} component={HairLossBarcelona} />
        <Route path={"/en/anti-aging-barcelona"} component={AntiAgingBarcelona} />
        <Route path={"/en/pricing"} component={Tarifas} />
        <Route path={"/en/dermatologist-barcelona"} component={DermatologistBarcelona} />
        <Route path={"/en/mole-check-barcelona"} component={MoleCheckBarcelona} />
        <Route path={"/en/skin-cancer-screening-barcelona"} component={SkinCancerScreeningBarcelona} />
        <Route path={"/en/melanoma-specialist-barcelona"} component={MelanomaSpecialistBarcelona} />
        <Route path={"/en/ads/hair-loss-treatment-barcelona"} component={HairLossTreatmentBarcelona} />
        <Route path={"/en/ads/acne-treatment-barcelona"} component={AcneTreatmentBarcelona} />
        <Route path={"/en/rosacea-treatment-barcelona"} component={RosaceaTreatmentBarcelona} />
        <Route path={"/en/botox-barcelona-treatment"} component={BotoxBarcelonaSqueeze} />
        <Route path={"/en/english-speaking-dermatologist-barcelona"} component={EnglishSpeakingDermatologistBarcelona} />
        <Route path={"/en/thank-you"} component={Gracias} />

        {/* Swedish routes (/sv/*) - Swedish slugs */}
        <Route path={"/sv"} component={Home} />
        <Route path={"/sv/tjanster"} component={Services} />
        <Route path={"/sv/vanliga-fragor"} component={FAQ} />
        <Route path={"/sv/blogg/:slug"} component={BlogDetail} />
        <Route path={"/sv/blogg"} component={Blog} />
        <Route path={"/sv/kontakt"} component={Contact} />
        <Route path={"/sv/om-mig"} component={About} />
        <Route path={"/sv/konsultation"} component={SqueezePage} />
        <Route path={"/sv/konsultation/harfall"} component={SqueezeHairLoss} />
        <Route path={"/sv/konsultation/akne"} component={SqueezeAcne} />
        <Route path={"/sv/konsultation/leverfackar"} component={SqueezeMoles} />
        <Route path={"/sv/konsultation/laser"} component={SqueezeLaser} />
        <Route path={"/sv/omdomen"} component={Testimonials} />
        <Route path={"/sv/boka"} component={Booking} />
        <Route path={"/sv/videor"} component={Videos} />
        <Route path={"/sv/aquapure"} component={AquaPure} />
        <Route path={"/sv/estetiska-behandlingar"} component={AestheticTreatments} />
        <Route path={"/sv/co2-laser"} component={CO2Laser} />
        <Route path={"/sv/botox-barcelona"} component={BotoxBarcelona} />
        <Route path={"/sv/prp-barcelona"} component={PRPBarcelona} />
        <Route path={"/sv/aknebehandling-barcelona"} component={AcneBarcelona} />
        <Route path={"/sv/harfallsbehandling-barcelona"} component={HairLossBarcelona} />
        <Route path={"/sv/anti-aging-barcelona"} component={AntiAgingBarcelona} />
        <Route path={"/sv/priser"} component={Tarifas} />
        <Route path={"/sv/hudlakare-barcelona"} component={DermatologistBarcelona} />
        <Route path={"/sv/leverfackskontroll-barcelona"} component={MoleCheckBarcelona} />
        <Route path={"/sv/hudcancerscreening-barcelona"} component={SkinCancerScreeningBarcelona} />
        <Route path={"/sv/melanomspecialist-barcelona"} component={MelanomaSpecialistBarcelona} />
        <Route path={"/sv/ads/harfallsbehandling-barcelona"} component={HairLossTreatmentBarcelona} />
        <Route path={"/sv/ads/aknebehandling-barcelona"} component={AcneTreatmentBarcelona} />
        <Route path={"/sv/rosaceabehandling-barcelona"} component={RosaceaTreatmentBarcelona} />
        <Route path={"/sv/botox-barcelona-behandling"} component={BotoxBarcelonaSqueeze} />
        <Route path={"/sv/engelsktalande-hudlakare-barcelona"} component={EnglishSpeakingDermatologistBarcelona} />
        <Route path={"/sv/tack"} component={Gracias} />

        {/* Spanish routes (/es/* redirect support) */}
        <Route path={"/es"} component={Home} />
        <Route path={"/es/servicios"} component={Services} />
        <Route path={"/es/preguntas-frecuentes"} component={FAQ} />
        <Route path={"/es/blog/:slug"} component={BlogDetail} />
        <Route path={"/es/blog"} component={Blog} />
        <Route path={"/es/contacto"} component={Contact} />
        <Route path={"/es/sobre-mi"} component={About} />
        <Route path={"/es/consulta"} component={SqueezePage} />
        <Route path={"/es/consulta/caida-cabello"} component={SqueezeHairLoss} />
        <Route path={"/es/consulta/acne"} component={SqueezeAcne} />
        <Route path={"/es/consulta/lunares"} component={SqueezeMoles} />
        <Route path={"/es/consulta/laser"} component={SqueezeLaser} />
        <Route path={"/es/testimonios"} component={Testimonials} />
        <Route path={"/es/reservar"} component={Booking} />
        <Route path={"/es/videos"} component={Videos} />
        <Route path={"/es/aquapure"} component={AquaPure} />
        <Route path={"/es/tratamientos-esteticos"} component={AestheticTreatments} />
        <Route path={"/es/laser-co2"} component={CO2Laser} />
        <Route path={"/es/botox-barcelona"} component={BotoxBarcelona} />
        <Route path={"/es/prp-barcelona"} component={PRPBarcelona} />
        <Route path={"/es/tratamiento-acne-barcelona"} component={AcneBarcelona} />
        <Route path={"/es/tratamiento-capilar-barcelona"} component={HairLossBarcelona} />
        <Route path={"/es/anti-envejecimiento-barcelona"} component={AntiAgingBarcelona} />
        <Route path={"/es/tarifas"} component={Tarifas} />
        
        {/* Root routes (Spanish default - localized slugs) */}
        <Route path={"/"} component={Home} />
        <Route path={"/servicios"} component={Services} />
        <Route path={"/preguntas-frecuentes"} component={FAQ} />
        <Route path={"/blog/:slug"} component={BlogDetail} />
        <Route path={"/blog"} component={Blog} />
        <Route path={"/contacto"} component={Contact} />
        <Route path={"/sobre-mi"} component={About} />
        <Route path={"/consulta"} component={SqueezePage} />
        <Route path={"/consulta/caida-cabello"} component={SqueezeHairLoss} />
        <Route path={"/consulta/acne"} component={SqueezeAcne} />
        <Route path={"/consulta/lunares"} component={SqueezeMoles} />
        <Route path={"/consulta/laser"} component={SqueezeLaser} />
        <Route path={"/testimonios"} component={Testimonials} />
        <Route path={"/reservar"} component={Booking} />
        <Route path={"/videos"} component={Videos} />
        <Route path={"/aquapure"} component={AquaPure} />
        <Route path={"/tratamientos-esteticos"} component={AestheticTreatments} />
        <Route path={"/laser-co2"} component={CO2Laser} />
        <Route path={"/botox-barcelona"} component={BotoxBarcelona} />
        <Route path={"/prp-barcelona"} component={PRPBarcelona} />
        <Route path={"/tratamiento-acne-barcelona"} component={AcneBarcelona} />
        <Route path={"/tratamiento-capilar-barcelona"} component={HairLossBarcelona} />
        <Route path={"/anti-envejecimiento-barcelona"} component={AntiAgingBarcelona} />
        <Route path={"/tarifas"} component={Tarifas} />
        <Route path={"/dermatologo-barcelona"} component={DermatologistBarcelona} />
        <Route path={"/revision-lunares-barcelona"} component={MoleCheckBarcelona} />
        <Route path={"/deteccion-cancer-piel-barcelona"} component={SkinCancerScreeningBarcelona} />
        <Route path={"/especialista-melanoma-barcelona"} component={MelanomaSpecialistBarcelona} />
        <Route path={"/ads/tratamiento-capilar-barcelona"} component={HairLossTreatmentBarcelona} />
        <Route path={"/ads/tratamiento-acne-barcelona"} component={AcneTreatmentBarcelona} />
        <Route path={"/tratamiento-rosacea-barcelona"} component={RosaceaTreatmentBarcelona} />
        <Route path={"/botox-barcelona-tratamiento"} component={BotoxBarcelonaSqueeze} />
        <Route path={"/dermatologo-ingles-barcelona"} component={EnglishSpeakingDermatologistBarcelona} />
        <Route path={"/gracias"} component={Gracias} />
        
        {/* Swedish redirects: English slugs under /sv/ -> localized Swedish slugs */}
        <Route path="/sv/contact">{() => <Redirect to="/sv/kontakt" />}</Route>
        <Route path="/sv/services">{() => <Redirect to="/sv/tjanster" />}</Route>
        <Route path="/sv/faq">{() => <Redirect to="/sv/vanliga-fragor" />}</Route>
        <Route path="/sv/blog/:slug">{() => <BlogSlugRedirect targetLang="sv" targetBlogBase="blogg" />}</Route>
        <Route path="/sv/blog">{() => <Redirect to="/sv/blogg" />}</Route>
        <Route path="/sv/about">{() => <Redirect to="/sv/om-mig" />}</Route>
        <Route path="/sv/booking">{() => <Redirect to="/sv/boka" />}</Route>
        <Route path="/sv/pricing">{() => <Redirect to="/sv/priser" />}</Route>
        <Route path="/sv/testimonials">{() => <Redirect to="/sv/omdomen" />}</Route>
        <Route path="/sv/videos">{() => <Redirect to="/sv/videor" />}</Route>
        <Route path="/sv/aesthetic-treatments">{() => <Redirect to="/sv/estetiska-behandlingar" />}</Route>
        <Route path="/sv/squeeze">{() => <Redirect to="/sv/konsultation" />}</Route>
        <Route path="/sv/squeeze/hair-loss">{() => <Redirect to="/sv/konsultation/harfall" />}</Route>
        <Route path="/sv/squeeze/acne">{() => <Redirect to="/sv/konsultation/akne" />}</Route>
        <Route path="/sv/squeeze/moles">{() => <Redirect to="/sv/konsultation/leverfackar" />}</Route>
        <Route path="/sv/squeeze/laser">{() => <Redirect to="/sv/konsultation/laser" />}</Route>
        <Route path="/sv/thank-you">{() => <Redirect to="/sv/tack" />}</Route>
        <Route path="/sv/hair-loss-treatment-barcelona">{() => <Redirect to="/sv/harfallsbehandling-barcelona" />}</Route>
        <Route path="/sv/acne-treatment-barcelona">{() => <Redirect to="/sv/aknebehandling-barcelona" />}</Route>
        <Route path="/sv/anti-aging-barcelona">{() => <Redirect to="/sv/anti-aging-barcelona" />}</Route>
        <Route path="/sv/dermatologist-barcelona">{() => <Redirect to="/sv/hudlakare-barcelona" />}</Route>
        <Route path="/sv/mole-check-barcelona">{() => <Redirect to="/sv/leverfackskontroll-barcelona" />}</Route>
        <Route path="/sv/skin-cancer-screening-barcelona">{() => <Redirect to="/sv/hudcancerscreening-barcelona" />}</Route>
        <Route path="/sv/melanoma-specialist-barcelona">{() => <Redirect to="/sv/melanomspecialist-barcelona" />}</Route>
        <Route path="/sv/rosacea-treatment-barcelona">{() => <Redirect to="/sv/rosaceabehandling-barcelona" />}</Route>
        <Route path="/sv/botox-barcelona-treatment">{() => <Redirect to="/sv/botox-barcelona-behandling" />}</Route>
        <Route path="/sv/english-speaking-dermatologist-barcelona">{() => <Redirect to="/sv/engelsktalande-hudlakare-barcelona" />}</Route>

        {/* English redirects: Spanish slugs under /en/ -> localized English slugs */}
        <Route path="/en/contacto">{() => <Redirect to="/en/contact" />}</Route>
        <Route path="/en/servicios">{() => <Redirect to="/en/services" />}</Route>
        <Route path="/en/preguntas-frecuentes">{() => <Redirect to="/en/faq" />}</Route>
        <Route path="/en/sobre-mi">{() => <Redirect to="/en/about" />}</Route>
        <Route path="/en/reservar">{() => <Redirect to="/en/booking" />}</Route>
        <Route path="/en/tarifas">{() => <Redirect to="/en/pricing" />}</Route>
        <Route path="/en/testimonios">{() => <Redirect to="/en/testimonials" />}</Route>
        <Route path="/en/consulta">{() => <Redirect to="/en/squeeze" />}</Route>
        <Route path="/en/tratamientos-esteticos">{() => <Redirect to="/en/aesthetic-treatments" />}</Route>
        <Route path="/en/laser-co2">{() => <Redirect to="/en/co2-laser" />}</Route>
        <Route path="/en/gracias">{() => <Redirect to="/en/thank-you" />}</Route>
        <Route path="/en/tratamiento-acne-barcelona">{() => <Redirect to="/en/acne-treatment-barcelona" />}</Route>
        <Route path="/en/tratamiento-capilar-barcelona">{() => <Redirect to="/en/hair-loss-treatment-barcelona" />}</Route>
        <Route path="/en/dermatologo-barcelona">{() => <Redirect to="/en/dermatologist-barcelona" />}</Route>
        <Route path="/en/revision-lunares-barcelona">{() => <Redirect to="/en/mole-check-barcelona" />}</Route>
        <Route path="/en/deteccion-cancer-piel-barcelona">{() => <Redirect to="/en/skin-cancer-screening-barcelona" />}</Route>
        <Route path="/en/especialista-melanoma-barcelona">{() => <Redirect to="/en/melanoma-specialist-barcelona" />}</Route>
        <Route path="/en/tratamiento-rosacea-barcelona">{() => <Redirect to="/en/rosacea-treatment-barcelona" />}</Route>

        {/* Spanish root redirects: English slugs at root -> localized Spanish slugs */}
        <Route path="/contact">{() => <Redirect to="/contacto" />}</Route>
        <Route path="/services">{() => <Redirect to="/servicios" />}</Route>
        <Route path="/faq">{() => <Redirect to="/preguntas-frecuentes" />}</Route>
        <Route path="/about">{() => <Redirect to="/sobre-mi" />}</Route>
        <Route path="/booking">{() => <Redirect to="/reservar" />}</Route>
        <Route path="/pricing">{() => <Redirect to="/tarifas" />}</Route>
        <Route path="/testimonials">{() => <Redirect to="/testimonios" />}</Route>
        <Route path="/squeeze">{() => <Redirect to="/consulta" />}</Route>
        <Route path="/aesthetic-treatments">{() => <Redirect to="/tratamientos-esteticos" />}</Route>
        <Route path="/co2-laser">{() => <Redirect to="/laser-co2" />}</Route>
        <Route path="/thank-you">{() => <Redirect to="/gracias" />}</Route>

        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const [location] = useLocation();
  const [language, setLanguage] = useState<'en' | 'es' | 'sv'>(() => {
    // Determine language from URL
    if (location.startsWith('/en')) return 'en';
    if (location.startsWith('/sv')) return 'sv';
    return 'es'; // Spanish is default
  });

  // Update language when URL changes
  useEffect(() => {
    if (location.startsWith('/en')) {
      setLanguage('en');
    } else if (location.startsWith('/sv')) {
      setLanguage('sv');
    } else {
      setLanguage('es');
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <LanguageProvider initialLanguage={language}>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <CanonicalHead />
            <Router />
            <WhatsAppButton />
            <MobileCTABar />
          </TooltipProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
